import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private expirationTimes: {[key: string]: number} = {};
  
  constructor(@Inject(PLATFORM_ID) private platformId: any) {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  setItem(key: string, value: any, hoursToExpire: number = 1): void {
    if (!this.isBrowser()) return;
    
    const expirationTime = new Date().getTime() + (hoursToExpire * 60 * 60 * 1000);
    const data = {
      value: value,
      expiration: expirationTime
    };
    
    try {
      localStorage.setItem(key, btoa(JSON.stringify(data)));
      this.expirationTimes[key] = expirationTime;
    } catch (error) {
      console.error('Error storing item:', error);
    }
  }

  getItem(key: string): any | null {
    if (!this.isBrowser()) return null;
    
    try {
      const encoded = localStorage.getItem(key);
      if (!encoded) return null;
      
      const decoded = JSON.parse(atob(encoded));
      
      if (new Date().getTime() > decoded.expiration) {
        this.removeItem(key);
        return null;
      }
      
      return decoded.value;
    } catch (error) {
      console.error('Error parsing stored item:', error);
      return null;
    }
  }

  removeItem(key: string): void {
    if (!this.isBrowser()) return;
    
    try {
      localStorage.removeItem(key);
      delete this.expirationTimes[key];
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  // simple storage without expiration
  setSimpleItem(key: string, value: any): void {
    if (!this.isBrowser()) return;
    
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error storing simple item:', error);
    }
  }

  // simple storage retrieval
  getSimpleItem(key: string): any | null {
    if (!this.isBrowser()) return null;
    
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Error retrieving simple item:', error);
      return null;
    }
  }

  cleanupExpired(): void {
    if (!this.isBrowser()) return;
    
    Object.keys(this.expirationTimes).forEach(key => {
      if (new Date().getTime() > this.expirationTimes[key]) {
        this.removeItem(key);
      }
    });
  }

  getStorageKey(key: string): string {
    return `${key}`;
  }

  // to check if item exists
  hasItem(key: string): boolean {
    if (!this.isBrowser()) return false;
    return localStorage.getItem(key) !== null;
  }

  // to clear all storage items (useful for logout)
  clear(): void {
    if (!this.isBrowser()) return;
    
    try {
      localStorage.clear();
      this.expirationTimes = {};
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }
}